import { anySignal } from "@-/com.npmjs.any-signal";
import {
  DeadlineExceededError,
  fetchWithDeadline,
  notifyDeadlineExceeded,
} from "@-/dev.improve.haniverse.lib.deadline";
import { getOrpcProcedureDeadlineMs, ORPC_DEFAULT_DEADLINE_MS } from "#orpc-procedure-deadlines";

/** oRPC's `BatchLinkPlugin` posts every batch to `<rpc url>/__batch__`. */
const BATCH_PATH_SUFFIX = "/__batch__";

/**
 * Re-emit a streamed body, calling `onSettled` exactly once when it ends by any route (drained,
 * errored or cancelled by its reader), so the deadline timer below never outlives the response.
 */
function streamWithSettlement(
  body: ReadableStream<Uint8Array>,
  onSettled: () => void,
): ReadableStream<Uint8Array> {
  const reader = body.getReader();
  return new ReadableStream<Uint8Array>({
    cancel: async (reason: unknown) => {
      onSettled();
      await reader.cancel(reason);
    },
    pull: async (controller) => {
      try {
        const { done, value } = await reader.read();
        if (done) {
          onSettled();
          controller.close();
          return;
        }
        controller.enqueue(value);
      } catch (error) {
        onSettled();
        controller.error(error);
      }
    },
  });
}

/**
 * A STREAMING batch (the web default) answers each member as soon as its procedure finishes, so a
 * slow or hung member must not hold its fast siblings — which buffering the body would do. The
 * deadline therefore covers headers AND the whole streamed body without buffering it: at the
 * deadline the request aborts, the body errors with a `DeadlineExceededError`, and only the members
 * that had not yet been answered reject. Members already delivered keep their results.
 */
async function fetchStreamingBatchWithDeadline(
  request: Request,
  init: RequestInit,
  operation: string,
): Promise<Response> {
  const deadlineMs = ORPC_DEFAULT_DEADLINE_MS;
  const deadline = new AbortController();
  const signal = anySignal([request.signal, deadline.signal]);
  let settled = false;
  const timer = setTimeout(() => {
    if (settled) {
      return;
    }
    settled = true;
    const error = new DeadlineExceededError(operation, deadlineMs);
    notifyDeadlineExceeded(error);
    // Aborting with the error as the reason surfaces it from the pending `fetch` AND from any read
    // of the body in flight, so every unanswered member gets a "timed out" (retryable) failure.
    deadline.abort(error);
  }, deadlineMs);
  const settle = (): void => {
    if (settled) {
      return;
    }
    settled = true;
    clearTimeout(timer);
    signal.clear();
  };
  try {
    const response = await fetch(request, { ...init, signal });
    if (response.body === null) {
      settle();
      return response;
    }
    return new Response(streamWithSettlement(response.body, settle), {
      headers: response.headers,
      status: response.status,
      statusText: response.statusText,
    });
  } catch (error) {
    settle();
    throw error;
  }
}

/**
 * The oRPC link's `fetch`: every round trip is bounded, headers and body.
 *
 * - An ordinary call gets its procedure's budget (`orpc-procedure-deadlines.ts`) and a buffered
 *   body, so oRPC's later decode can never stall outside the deadline.
 * - A BUFFERED batch (React Native, Node) holds every member until the slowest answers. Slow
 *   procedures are never batched (see `orpc-link-plugins.ts`), so a batch only ever contains
 *   default-budget calls and shares that budget; a member that hangs fails the batch at the
 *   deadline rather than holding it forever. That one hung member can delay its siblings by up to
 *   that budget is the price of batching on a runtime that cannot stream the response.
 * - A STREAMING batch (web) releases each member as it lands; see
 *   {@link fetchStreamingBatchWithDeadline}.
 *
 * The caller's own signal (`request.signal`) is honored in every case.
 */
export async function fetchOrpcWithDeadline(
  request: Request,
  init: RequestInit,
  path: readonly string[],
): Promise<Response> {
  const isBatch = new URL(request.url).pathname.endsWith(BATCH_PATH_SUFFIX);
  const operation = isBatch ? "oRPC batch" : `oRPC ${path.join(".")}`;
  if (isBatch && request.headers.get("x-orpc-batch") === "streaming") {
    return fetchStreamingBatchWithDeadline(request, init, operation);
  }
  return fetchWithDeadline(request, {
    ...init,
    deadline: isBatch ? ORPC_DEFAULT_DEADLINE_MS : getOrpcProcedureDeadlineMs(path),
    operation,
    // A `signal` in `init` replaces the Request's own, so pass the caller's through explicitly.
    signal: request.signal,
  });
}
